import { AlertCircle, ChevronRight, Clock, History, User } from "lucide-react";
import { useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

import { ActivityLogDetailDialog } from "./activity-log-detail-dialog";
import {
  formatEntityName,
  formatTimeWIB,
  getActionMeta,
  getActivitySummary,
  groupLogsByDay,
} from "./activity-log-utils";
import { useEntityActivityLogs } from "./use-activity-logs";

import type { ActivityLog } from "@artisancode/api-types";

export type ActivityEntityName = "sale" | "rider_sale" | "expense";

export interface EntityActivityLogDialogProps {
  entityName: ActivityEntityName;
  entityId: string | null | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
}

export function EntityActivityLogDialog({
  entityName,
  entityId,
  open,
  onOpenChange,
  title = "Riwayat Aktivitas",
}: EntityActivityLogDialogProps) {
  const [selectedDetailLog, setSelectedDetailLog] =
    useState<ActivityLog | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const { data, isLoading, isError, refetch } = useEntityActivityLogs({
    entityName,
    entityId: entityId || undefined,
    enabled: open && Boolean(entityId),
  });

  const items = useMemo(() => {
    const list = data?.items ?? [];
    return [...list].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [data?.items]);

  const groups = useMemo(() => groupLogsByDay(items), [items]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[95vw] sm:max-w-xl max-h-[85vh] flex flex-col p-4 sm:p-6 overflow-hidden">
          <DialogHeader className="pb-3 border-b border-border space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <DialogTitle className="text-base sm:text-lg font-semibold">
                {title}
              </DialogTitle>
              <Badge variant="secondary" className="text-xs">
                {formatEntityName(entityName)}
              </Badge>
            </div>
            {entityId && (
              <DialogDescription className="text-xs text-muted-foreground truncate">
                ID Transaksi:{" "}
                <code className="font-mono text-[11px] bg-muted px-1.5 py-0.5 rounded border">
                  {entityId}
                </code>
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-[160px] max-h-[60vh] pr-1 mt-2">
            {isLoading ? (
              <div className="space-y-3 py-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className="p-3 border border-border/60 rounded-lg space-y-2 bg-muted/10"
                  >
                    <div className="flex justify-between items-center gap-2">
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                    <Skeleton className="h-4 w-44" />
                  </div>
                ))}
              </div>
            ) : isError ? (
              <div className="flex flex-col items-center justify-center py-10 text-center space-y-2">
                <AlertCircle className="w-8 h-8 text-destructive/80" />
                <p className="text-sm font-medium text-foreground">
                  Gagal memuat riwayat aktivitas
                </p>
                <p className="text-xs text-muted-foreground max-w-xs">
                  Terjadi kesalahan saat mengambil riwayat dari server.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void refetch()}
                  className="mt-2 text-xs"
                >
                  Coba lagi
                </Button>
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center space-y-2">
                <History className="w-8 h-8 text-muted-foreground/50" />
                <p className="text-sm font-medium text-muted-foreground">
                  Belum ada aktivitas
                </p>
                <p className="text-xs text-muted-foreground/80 max-w-xs">
                  Belum ada catatan perubahan atau aktivitas untuk transaksi
                  ini.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {groups.map((group) => (
                  <div key={group.dateLabel} className="space-y-2">
                    <div className="sticky top-0 bg-background/95 backdrop-blur-xs py-1 text-xs font-semibold text-muted-foreground border-b border-border/40 z-10">
                      {group.dateLabel}
                    </div>
                    <div className="space-y-2">
                      {group.items.map((log) => {
                        const actionMeta = getActionMeta(log.activity);
                        const summary = getActivitySummary(log);

                        return (
                          <button
                            key={log.id}
                            type="button"
                            onClick={() => {
                              setSelectedDetailLog(log);
                              setDetailOpen(true);
                            }}
                            className="w-full text-left p-3 rounded-lg border border-border/60 hover:border-border hover:bg-muted/50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring space-y-1.5 group cursor-pointer"
                          >
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                                <User className="w-3.5 h-3.5 text-primary shrink-0" />
                                <span className="text-xs font-medium text-foreground truncate">
                                  {log.userName || "System"}
                                </span>
                              </div>
                              <div className="flex items-center gap-1 text-[11px] text-muted-foreground shrink-0">
                                <Clock className="w-3 h-3" />
                                <span>{formatTimeWIB(log.createdAt)}</span>
                              </div>
                            </div>

                            <div className="flex items-center justify-between gap-2 pt-0.5">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <Badge
                                  variant="outline"
                                  className={`text-[10px] px-1.5 py-0 h-5 shrink-0 ${actionMeta.badgeClass}`}
                                >
                                  {actionMeta.label}
                                </Badge>
                                <span className="text-xs text-muted-foreground truncate">
                                  {summary}
                                </span>
                              </div>
                              <span className="text-xs text-primary shrink-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center">
                                Detail{" "}
                                <ChevronRight className="w-3 h-3 ml-0.5" />
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ActivityLogDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        log={selectedDetailLog}
      />
    </>
  );
}
